import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { automunityAPI } from '../lib/api';
import toast, { Toaster } from 'react-hot-toast';
import './Cleanup.css';

interface Automation {
  automation_id: number;
  name: string;
  description?: string;
  adl_user_id?: number;
  adl_name?: string;
  contributors?: any;
  stages?: any;
  services?: any;
  request_count?: number;
  team_names?: string;
  [key: string]: any;
}

interface User {
  user_id: number;
  full_name: string;
  is_developer: number;
}

interface Stage {
  stage_id: number;
  name: string;
}

interface Service {
  service_id: number;
  name: string;
}

const Cleanup: React.FC = () => {
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [developers, setDevelopers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('adl');
  const [adlFilter, setAdlFilter] = useState<string>('mine');
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [docText, setDocText] = useState<Record<number, string>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [autoRes, usersRes, stagesRes, servicesRes, rolesRes] = await Promise.all([
          automunityAPI.getAutomations(),
          automunityAPI.getUsers(),
          automunityAPI.getStages(),
          automunityAPI.getServices(),
          automunityAPI.getRoles()
        ]);
        const autoData = await autoRes.body.json();
        const userData = await usersRes.body.json();
        const stagesData = await stagesRes.body.json();
        const servicesData = await servicesRes.body.json();
        const rolesData = await rolesRes.body.json();

        let autos = Array.isArray(autoData) ? autoData
          : Array.isArray((autoData as any)?.records) ? (autoData as any).records
          : typeof (autoData as any)?.records === 'string' ? JSON.parse((autoData as any).records) : [];
        autos = autos.map((a: any) => ({
          ...a,
          contributors: typeof a.contributors === 'string' ? (() => { try { return JSON.parse(a.contributors); } catch { return null; } })() : a.contributors,
          stages: typeof a.stages === 'string' ? (() => { try { return JSON.parse(a.stages); } catch { return null; } })() : a.stages,
          services: typeof a.services === 'string' ? (() => { try { return JSON.parse(a.services); } catch { return null; } })() : a.services,
        }));
        setAutomations(autos.filter((a: any) => a && a.automation_id));

        const users = Array.isArray(userData) ? userData : (userData as any)?.records || [];
        setAllUsers(users.filter((u: any) => u.is_active));
        setDevelopers(users.filter((u: any) => u.is_developer && u.is_active));
        setStages(Array.isArray(stagesData) ? stagesData : (stagesData as any)?.records || []);
        setServices(Array.isArray(servicesData) ? servicesData : (servicesData as any)?.records || []);
        setRoles(Array.isArray(rolesData) ? rolesData : (rolesData as any)?.records || []);

        try {
          const session = await fetchAuthSession();
          const email = (session.tokens?.idToken?.payload?.email as string || '').toLowerCase();
          const match = users.find((u: any) => u.email?.toLowerCase() === email);
          if (match) setCurrentUserId(match.user_id);
        } catch { /* no-op */ }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAssignADL = async (automationId: number, userId: string) => {
    try {
      await automunityAPI.updateAutomation(String(automationId), { adl_user_id: userId || null });
      setAutomations(prev => prev.map(a =>
        a.automation_id === automationId
          ? { ...a, adl_user_id: userId ? Number(userId) : undefined, adl_name: developers.find(d => d.user_id === Number(userId))?.full_name }
          : a
      ));
      toast.success('ADL assigned');
    } catch { toast.error('Failed to update'); }
  };

  const handleAssignStage = async (automationId: number, stageId: string) => {
    if (!stageId) return;
    try {
      await automunityAPI.addAutomationStage(String(automationId), { stage_id: Number(stageId) });
      const stageName = stages.find(s => s.stage_id === Number(stageId))?.name || 'Set';
      setAutomations(prev => prev.map(a =>
        a.automation_id === automationId ? { ...a, stages: [{ stage_id: Number(stageId), name: stageName }] } : a
      ));
      toast.success(`Stage set to ${stageName}`);
    } catch { toast.error('Failed to set stage'); }
  };

  const handleAddService = async (automationId: number, serviceId: string) => {
    if (!serviceId) return;
    try {
      await automunityAPI.addAutomationService(String(automationId), { service_id: Number(serviceId) });
      setAutomations(prev => prev.map(a =>
        a.automation_id === automationId ? { ...a, services: [...(a.services || []), { service_id: Number(serviceId) }] } : a
      ));
      toast.success('Service added');
    } catch { toast.error('Failed to add service'); }
  };

  const handleAddContributor = async (automationId: number, userId: string, roleId: string) => {
    if (!userId || !roleId) return;
    const role = roles.find((r: any) => r.role_id === Number(roleId));
    const description = role?.description || role?.name || 'Contributor';
    try {
      await automunityAPI.createContribution(String(automationId), { user_id: Number(userId), role_id: Number(roleId), description });
      setAutomations(prev => prev.map(a =>
        a.automation_id === automationId ? { ...a, contributors: [...(a.contributors || []), { user_id: Number(userId) }] } : a
      ));
      toast.success('Contributor added');
    } catch { toast.error('Failed to add contributor'); }
  };

  const handleSaveDocumentation = async (automationId: number) => {
    const html = docText[automationId];
    if (!html?.trim()) { toast.error('Paste documentation HTML first'); return; }
    try {
      await automunityAPI.updateAutomation(String(automationId), { documentation_text: html });
      setAutomations(prev => prev.map(a =>
        a.automation_id === automationId ? { ...a, documentation_text: html } : a
      ));
      setDocText(prev => { const n = { ...prev }; delete n[automationId]; return n; });
      toast.success('Documentation saved');
    } catch { toast.error('Failed to save'); }
  };

  if (loading) return <div className="cleanup-loading">Loading automations...</div>;

  const missingADL = automations.filter(a => !a.adl_user_id);
  const filterByADL = (list: Automation[]) => {
    if (adlFilter === 'all') return list;
    const filterId = adlFilter === 'mine' ? currentUserId : Number(adlFilter);
    return list.filter(a => a.adl_user_id === filterId);
  };
  // Only surface automations that are in the "Production" stage for the
  // value-tracking cleanup tabs. Non-production automations are allowed to have
  // missing values and shouldn't clutter these lists. The "No Stage" and
  // "No ADL" tabs intentionally do NOT use this — they apply to all automations.
  const isProduction = (a: Automation) => {
    let stages = a.stages;
    if (typeof stages === 'string') { try { stages = JSON.parse(stages); } catch { stages = []; } }
    return Array.isArray(stages) && stages.some((s: any) => s && s.name === 'Production');
  };
  const productionAutomations = automations.filter(isProduction);
  const missingContributors = filterByADL(productionAutomations.filter(a => !a.contributors || (Array.isArray(a.contributors) && a.contributors.length === 0)));
  const missingRequests = filterByADL(productionAutomations.filter(a => !a.request_count || a.request_count === 0));
  const noServices = filterByADL(productionAutomations.filter(a => {
    if (!a.services) return true;
    const svc = typeof a.services === 'string' ? (() => { try { return JSON.parse(a.services); } catch { return null; } })() : a.services;
    if (!svc || !Array.isArray(svc)) return true;
    // JSON_ARRAYAGG can return [null] when no real services exist
    const realServices = svc.filter((s: any) => s && s.service_id);
    return realServices.length === 0;
  }));
  const noStage = filterByADL(automations.filter(a => {
    if (!a.stages) return true;
    const stg = Array.isArray(a.stages) ? a.stages : null;
    return !stg || stg.length === 0;
  }));
  const noFinancials = filterByADL(productionAutomations.filter(a => {
    return !a.valuation_category && !a.cost_per_invocation && !a.minute_gain_per_objective && !a.hourly_rate_gain_per_objective && !a.development_hours && !a.monthly_value_saved;
  }));
  const noDocs = filterByADL(productionAutomations.filter(a => !a.documentation_text));
  const totalIssues = missingADL.length + missingContributors.length + missingRequests.length + noServices.length + noStage.length + noFinancials.length + noDocs.length;

  return (
    <>
      <Toaster position="top-right" />
      <div className="cleanup-page">
        <div className="cleanup-header">
          <h1>🧹 Automation Cleanup</h1>
          <p className="cleanup-subtitle">{totalIssues} items need attention across {automations.length} automations ({productionAutomations.length} in Production)</p>
        </div>

        <div className="cleanup-tabs">
          <button className={activeTab === 'adl' ? 'active' : ''} onClick={() => setActiveTab('adl')}>
            👤 No ADL <span className="tab-count">{missingADL.length}</span>
          </button>
          <button className={activeTab === 'contributors' ? 'active' : ''} onClick={() => setActiveTab('contributors')}>
            👥 No Contributors <span className="tab-count">{missingContributors.length}</span>
          </button>
          <button className={activeTab === 'services' ? 'active' : ''} onClick={() => setActiveTab('services')}>
            🔌 No Services <span className="tab-count">{noServices.length}</span>
          </button>
          <button className={activeTab === 'requests' ? 'active' : ''} onClick={() => setActiveTab('requests')}>
            📋 No Requests <span className="tab-count">{missingRequests.length}</span>
          </button>
          <button className={activeTab === 'stage' ? 'active' : ''} onClick={() => setActiveTab('stage')}>
            🏷️ No Stage <span className="tab-count">{noStage.length}</span>
          </button>
          <button className={activeTab === 'financials' ? 'active' : ''} onClick={() => setActiveTab('financials')}>
            💰 No Financials <span className="tab-count">{noFinancials.length}</span>
          </button>
          <button className={activeTab === 'docs' ? 'active' : ''} onClick={() => setActiveTab('docs')}>
            📖 No Docs <span className="tab-count">{noDocs.length}</span>
          </button>
        </div>

        <div className="cleanup-content">
          {activeTab !== 'adl' && (
            <div className="cleanup-filter">
              <label>Filter by ADL:</label>
              <select value={adlFilter} onChange={e => setAdlFilter(e.target.value)}>
                <option value="mine">My Automations</option>
                <option value="all">All Developers</option>
                {developers.map(d => <option key={d.user_id} value={d.user_id}>{d.full_name}</option>)}
              </select>
            </div>
          )}
          {activeTab === 'adl' && (missingADL.length > 0 ? (
            <table className="cleanup-table"><thead><tr><th>Name</th><th>Description</th><th>Assign ADL</th><th></th></tr></thead><tbody>
              {missingADL.map(a => (
                <tr key={a.automation_id}>
                  <td className="auto-name">{a.name}</td>
                  <td className="auto-desc">{a.description?.substring(0, 80) || '—'}</td>
                  <td><select className="cleanup-select" value="" onChange={e => handleAssignADL(a.automation_id, e.target.value)}>
                    <option value="">Select developer...</option>
                    {developers.map(d => <option key={d.user_id} value={d.user_id}>{d.full_name}</option>)}
                  </select></td>
                  <td><Link to={`/automations/view/${a.automation_id}`} className="view-link">View</Link></td>
                </tr>
              ))}
            </tbody></table>
          ) : <p className="all-good">✅ All automations have an ADL assigned!</p>)}

          {activeTab === 'contributors' && (missingContributors.length > 0 ? (
            <table className="cleanup-table"><thead><tr><th>Name</th><th>Description</th><th>Add Contributor</th><th></th></tr></thead><tbody>
              {missingContributors.map(a => (
                <tr key={a.automation_id}>
                  <td className="auto-name">{a.name}</td>
                  <td className="auto-desc">{a.description?.substring(0, 80) || '—'}</td>
                  <td style={{ display: 'flex', gap: '4px' }}>
                    <select className="cleanup-select" id={`contrib-user-${a.automation_id}`} defaultValue="">
                      <option value="">User...</option>
                      {[...allUsers].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map(u => <option key={u.user_id} value={u.user_id}>{u.full_name}</option>)}
                    </select>
                    <select className="cleanup-select" id={`contrib-role-${a.automation_id}`} defaultValue="">
                      <option value="">Role...</option>
                      {roles.map((r: any) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
                    </select>
                    <button className="cleanup-btn" onClick={() => {
                      const u = (document.getElementById(`contrib-user-${a.automation_id}`) as HTMLSelectElement)?.value;
                      const r = (document.getElementById(`contrib-role-${a.automation_id}`) as HTMLSelectElement)?.value;
                      handleAddContributor(a.automation_id, u, r);
                    }}>Add</button>
                  </td>
                  <td><Link to={`/automations/view/${a.automation_id}`} className="view-link">View</Link></td>
                </tr>
              ))}
            </tbody></table>
          ) : <p className="all-good">✅ All Production automations have contributors!</p>)}

          {activeTab === 'services' && (noServices.length > 0 ? (
            <table className="cleanup-table"><thead><tr><th>Name</th><th>Description</th><th>Add Service</th><th></th></tr></thead><tbody>
              {noServices.map(a => (
                <tr key={a.automation_id}>
                  <td className="auto-name">{a.name}</td>
                  <td className="auto-desc">{a.description?.substring(0, 100) || '—'}</td>
                  <td>
                    <select className="cleanup-select" onChange={e => { handleAddService(a.automation_id, e.target.value); e.target.value = ''; }}>
                      <option value="">Add service...</option>
                      {services.map((s: any) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td><Link to={`/automations/view/${a.automation_id}`} className="view-link">View</Link></td>
                </tr>
              ))}
            </tbody></table>
          ) : <p className="all-good">✅ All Production automations have services!</p>)}

          {activeTab === 'requests' && (missingRequests.length > 0 ? (
            <table className="cleanup-table"><thead><tr><th>Name</th><th>Description</th><th></th></tr></thead><tbody>
              {missingRequests.map(a => (
                <tr key={a.automation_id}>
                  <td className="auto-name">{a.name}</td>
                  <td className="auto-desc">{a.description?.substring(0, 100) || '—'}</td>
                  <td><Link to={`/automations/view/${a.automation_id}`} className="view-link">View</Link></td>
                </tr>
              ))}
            </tbody></table>
          ) : <p className="all-good">✅ All Production automations have requests!</p>)}

          {activeTab === 'stage' && (noStage.length > 0 ? (
            <table className="cleanup-table"><thead><tr><th>Name</th><th>Description</th><th>Set Stage</th><th></th></tr></thead><tbody>
              {noStage.map(a => (
                <tr key={a.automation_id}>
                  <td className="auto-name">{a.name}</td>
                  <td className="auto-desc">{a.description?.substring(0, 100) || '—'}</td>
                  <td>
                    <select className="cleanup-select" onChange={e => { handleAssignStage(a.automation_id, e.target.value); }}>
                      <option value="">Select stage...</option>
                      {stages.map((s: any) => <option key={s.stage_id} value={s.stage_id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td><Link to={`/automations/view/${a.automation_id}`} className="view-link">View</Link></td>
                </tr>
              ))}
            </tbody></table>
          ) : <p className="all-good">✅ All automations have a stage set!</p>)}

          {activeTab === 'financials' && (noFinancials.length > 0 ? (
            <div>
              {noFinancials.map(a => (
                <div key={a.automation_id} className="doc-entry">
                  <div className="doc-entry-header">
                    <strong>{a.name}</strong>
                    <Link to={`/automations/view/${a.automation_id}`} className="view-link">View</Link>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: '#64748b' }}>Valuation Category</label>
                      <select className="cleanup-select" id={`fin-cat-${a.automation_id}`} defaultValue="">
                        <option value="">Select...</option>
                        <option value="1">Time Saved</option>
                        <option value="2">Risk Reduction</option>
                        <option value="3">Cost Optimization</option>
                        <option value="4">Enablement</option>
                        <option value="5">New Process</option>
                        <option value="6">Product</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#64748b' }}>Cost per Invocation ($)</label>
                      <input className="cleanup-select" type="text" id={`fin-cpi-${a.automation_id}`} placeholder="0.00" />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#64748b' }}>Time Saved (mins)</label>
                      <input className="cleanup-select" type="text" id={`fin-ts-${a.automation_id}`} placeholder="e.g., 15" />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#64748b' }}>Hourly Rate ($)</label>
                      <input className="cleanup-select" type="text" id={`fin-hr-${a.automation_id}`} placeholder="e.g., 150" />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#64748b' }}>Development Hours</label>
                      <input className="cleanup-select" type="text" id={`fin-dh-${a.automation_id}`} placeholder="e.g., 40" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button className="cleanup-btn" onClick={async () => {
                        const data: any = {};
                        const cat = (document.getElementById(`fin-cat-${a.automation_id}`) as HTMLSelectElement)?.value;
                        const cpi = (document.getElementById(`fin-cpi-${a.automation_id}`) as HTMLInputElement)?.value;
                        const ts = (document.getElementById(`fin-ts-${a.automation_id}`) as HTMLInputElement)?.value;
                        const hr = (document.getElementById(`fin-hr-${a.automation_id}`) as HTMLInputElement)?.value;
                        const dh = (document.getElementById(`fin-dh-${a.automation_id}`) as HTMLInputElement)?.value;
                        if (cat) data.valuation_category = cat;
                        if (cpi) data.cost_per_invocation = cpi;
                        if (ts) data.minute_gain_per_objective = ts;
                        if (hr) data.hourly_rate_gain_per_objective = hr;
                        if (dh) data.development_hours = dh;
                        if (Object.keys(data).length === 0) { toast.error('Fill in at least one field'); return; }
                        try {
                          await automunityAPI.updateAutomation(String(a.automation_id), data);
                          setAutomations(prev => prev.map(x => x.automation_id === a.automation_id ? { ...x, ...data } : x));
                          toast.success('Financials saved');
                        } catch { toast.error('Failed to save'); }
                      }}>Save</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="all-good">✅ All Production automations have financial data!</p>)}

          {activeTab === 'docs' && (noDocs.length > 0 ? (
            <div>
              <div className="docs-instructions">
                <p><strong>Generate documentation with Kiro:</strong></p>
                <ol>
                  <li>Open the automation's source files in Kiro</li>
                  <li>Tell Kiro: <em>"Generate HTML documentation for [automation name] and upload it to Automunity"</em></li>
                  <li>Kiro will review the code, generate docs, and save them via the API automatically</li>
                </ol>
                <p style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                  Or paste HTML manually below as a fallback:
                </p>
              </div>
              {noDocs.map(a => (
                <div key={a.automation_id} className="doc-entry">
                  <div className="doc-entry-header">
                    <strong>{a.name}</strong>
                    <Link to={`/automations/view/${a.automation_id}`} className="view-link">View</Link>
                  </div>
                  <textarea
                    className="doc-textarea"
                    placeholder="Paste generated HTML documentation here..."
                    value={docText[a.automation_id] || ''}
                    onChange={e => setDocText(prev => ({ ...prev, [a.automation_id]: e.target.value }))}
                    rows={4}
                  />
                  <button
                    className="cleanup-btn"
                    onClick={() => handleSaveDocumentation(a.automation_id)}
                    disabled={!docText[a.automation_id]?.trim()}
                  >
                    Save Documentation
                  </button>
                </div>
              ))}
            </div>
          ) : <p className="all-good">✅ All Production automations have documentation!</p>)}
        </div>
      </div>
    </>
  );
};

export default Cleanup;
